import {
  ChatSessionFileReferenceResponse,
  ChatSessionMessageResponse,
  ChatSessionResponse,
  ChatUploadDescriptorRequest,
  nullIfEmpty,
} from '@automatick/shared-ng';
import { isCanonicalUuid } from '@automatick/shared-client-ts';
import {
  ConversationPulseStage,
  RealtimeRenderEventType,
  RealtimeRenderStage,
} from '@automatick/shared-ts';
import { WebChatStateStore } from './web-chat-state.store';
import {
  DEFAULT_STREAM_MODE,
  NO_STREAM_MODE,
  WEBCHAT_CONVERSATION_ID,
  WebChatRenderEvent,
} from './web-chat.models';

export class WebChatMessageRuntime {
  constructor(private readonly state: WebChatStateStore) {}

  resolveStreamMode(): string {
    if (!this.stateSubject.value.uxCapabilities?.supportsStreamingResponses) {
      return NO_STREAM_MODE;
    }

    return DEFAULT_STREAM_MODE;
  }

  shouldIncludeThoughtProcess(): boolean {
    return (
      this.stateSubject.value.uxCapabilities
        ?.supportsThoughtProcessStreaming === true
    );
  }

  upsertSession(session: ChatSessionResponse, activate = false): void {
    const sessionId = session.sessionId;
    if (!sessionId) {
      return;
    }

    const sessions = [...this.stateSubject.value.sessions];
    const existingIndex = sessions.findIndex(
      (candidate) => candidate.sessionId === sessionId,
    );
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.unshift(session);
    }

    const shouldReplaceCurrentSession =
      activate || this.stateSubject.value.session?.sessionId === sessionId;

    this.patch({
      sessions,
      session: shouldReplaceCurrentSession
        ? session
        : this.stateSubject.value.session,
      selectedProjectId: shouldReplaceCurrentSession
        ? (session.projectId ?? null)
        : this.stateSubject.value.selectedProjectId,
      uxCapabilities:
        shouldReplaceCurrentSession && session.uxCapabilities
          ? session.uxCapabilities
          : this.stateSubject.value.uxCapabilities,
    });
  }

  buildConversationId(): string {
    return `${WEBCHAT_CONVERSATION_ID}-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
  }

  buildSessionTitleFromFirstMessage(
    content: string,
    uploadCount: number,
  ): string {
    const normalizedContent = content.replace(/\s+/g, ' ').trim();
    if (normalizedContent.length > 0) {
      return normalizedContent.slice(0, 80);
    }

    if (uploadCount > 0) {
      return `Attachments (${uploadCount})`;
    }

    return '';
  }

  sortMessagesChronologically(
    messages: ChatSessionMessageResponse[],
  ): ChatSessionMessageResponse[] {
    return messages
      .map((msg, idx) => ({ msg, idx }))
      .sort((a, b) => {
        const aTime = Date.parse(a.msg.occurredOn ?? '') || 0;
        const bTime = Date.parse(b.msg.occurredOn ?? '') || 0;
        if (aTime !== bTime) return aTime - bTime;
        // Stable tiebreaker: outbound (user) before inbound (AI) for same timestamp
        const aDir = a.msg.direction === 'Outbound' ? 0 : 1;
        const bDir = b.msg.direction === 'Outbound' ? 0 : 1;
        if (aDir !== bDir) return aDir - bDir;
        return a.idx - b.idx;
      })
      .map((x) => x.msg);
  }

  sortSessionFilesChronologically(
    files: ChatSessionFileReferenceResponse[],
  ): ChatSessionFileReferenceResponse[] {
    return files
      .map((file, idx) => ({ file, idx }))
      .sort((a, b) => {
        const aTime = Date.parse(a.file.occurredOn ?? '') || 0;
        const bTime = Date.parse(b.file.occurredOn ?? '') || 0;
        if (aTime !== bTime) {
          return bTime - aTime;
        }

        const aId = a.file.attachmentId ?? '';
        const bId = b.file.attachmentId ?? '';
        if (aId !== bId) {
          return String(bId).localeCompare(String(aId));
        }

        return a.idx - b.idx;
      })
      .map((item) => item.file);
  }

  /**
   * Merges server messages with existing local messages.
   * - Replaces optimistic local messages with their server counterpart (matched by idempotencyKey)
   * - Updates existing messages that changed (matched by entryId)
   * - Appends truly new messages from the server
   * This prevents full-array replacement which causes unnecessary re-renders.
   */
  mergeMessages(
    existing: ChatSessionMessageResponse[],
    incoming: ChatSessionMessageResponse[],
  ): ChatSessionMessageResponse[] {
    const incomingById = new Map<string, ChatSessionMessageResponse>();
    const incomingByIdempotency = new Map<string, ChatSessionMessageResponse>();
    for (const msg of incoming) {
      if (msg.entryId) {
        incomingById.set(msg.entryId, msg);
      }
      if (msg.idempotencyKey) {
        incomingByIdempotency.set(msg.idempotencyKey, msg);
      }
    }

    const mergedIds = new Set<string>();
    const merged: ChatSessionMessageResponse[] = [];

    // Walk existing messages and update/replace as needed
    for (const local of existing) {
      const isOptimistic = local.entryId?.startsWith('local-');

      // Try to match optimistic messages by idempotencyKey
      if (isOptimistic && local.idempotencyKey) {
        const serverVersion = incomingByIdempotency.get(local.idempotencyKey);
        if (serverVersion) {
          merged.push(this.mergeMessageRenderPayload(local, serverVersion));
          if (serverVersion.entryId) mergedIds.add(serverVersion.entryId);
          if (serverVersion.idempotencyKey)
            mergedIds.add(`idem:${serverVersion.idempotencyKey}`);
          continue;
        }
        // Keep optimistic message if server hasn't acknowledged it yet
        merged.push(local);
        continue;
      }

      // Try to match by entryId for updates
      if (local.entryId) {
        const serverVersion = incomingById.get(local.entryId);
        if (!serverVersion) {
          merged.push(local);
          mergedIds.add(local.entryId);
          if (local.idempotencyKey)
            mergedIds.add(`idem:${local.idempotencyKey}`);
          continue;
        }

        merged.push(this.mergeMessageRenderPayload(local, serverVersion));
        mergedIds.add(local.entryId);
        if (serverVersion.idempotencyKey)
          mergedIds.add(`idem:${serverVersion.idempotencyKey}`);
        continue;
      }

      // Keep local message as-is (not in server response — could be optimistic or older)
      merged.push(local);
      if (local.entryId) mergedIds.add(local.entryId);
      if (local.idempotencyKey) mergedIds.add(`idem:${local.idempotencyKey}`);
    }

    // Append truly new server messages not yet in the merged set
    for (const serverMsg of incoming) {
      const alreadyMerged =
        (serverMsg.entryId && mergedIds.has(serverMsg.entryId)) ||
        (serverMsg.idempotencyKey &&
          mergedIds.has(`idem:${serverMsg.idempotencyKey}`));
      if (!alreadyMerged) {
        merged.push(serverMsg);
      }
    }

    return this.sortMessagesChronologically(merged);
  }

  mergeMessageRenderPayload(
    existing: ChatSessionMessageResponse,
    incoming: ChatSessionMessageResponse,
  ): ChatSessionMessageResponse {
    return {
      ...incoming,
      attachments: this.hasItems(incoming.attachments)
        ? incoming.attachments
        : existing.attachments,
      presentations: this.hasItems(incoming.presentations)
        ? incoming.presentations
        : existing.presentations,
    };
  }

  hasItems<T>(items: T[] | null | undefined): items is T[] {
    return Array.isArray(items) && items.length > 0;
  }

  mergeSessionFiles(
    existing: ChatSessionFileReferenceResponse[],
    incoming: ChatSessionFileReferenceResponse[],
  ): ChatSessionFileReferenceResponse[] {
    const merged = new Map<string, ChatSessionFileReferenceResponse>();

    for (const file of [...existing, ...incoming]) {
      const key = this.buildSessionFileKey(file);
      if (!key) {
        continue;
      }

      merged.set(key, file);
    }

    return this.sortSessionFilesChronologically([...merged.values()]);
  }

  collectSessionFilesFromMessages(
    messages: ChatSessionMessageResponse[],
  ): ChatSessionFileReferenceResponse[] {
    return messages.flatMap((message) => message.attachments ?? []);
  }

  buildSessionFileKey(file: ChatSessionFileReferenceResponse): string | null {
    const attachmentId = file.attachmentId;
    if (
      typeof attachmentId === 'string' &&
      attachmentId.length > 0 &&
      attachmentId === attachmentId.trim()
    ) {
      return `attachment:${attachmentId}`;
    }

    if (attachmentId != null) {
      return null;
    }

    const storageModifiedName = this.readAssetString(
      file.asset?.storageModifiedName,
    );
    if (storageModifiedName) {
      return `asset:${storageModifiedName.toLowerCase()}`;
    }

    return null;
  }

  buildOptimisticAttachments(
    sessionId: string,
    entryId: string,
    uploads: ChatUploadDescriptorRequest[],
  ): ChatSessionFileReferenceResponse[] {
    if (uploads.length === 0) {
      return [];
    }

    const occurredOn = new Date().toISOString();
    return uploads.map((upload, index) => {
      const asset = upload.asset ?? {};
      const contentType = this.readAssetString(asset.contentType);
      const fileName = this.readAssetString(asset.fileName);
      const sizeBytes = this.readAssetNumber(asset.sizeBytes);

      return {
        attachmentId: crypto.randomUUID(),
        sessionId,
        entryId,
        direction: 'Outbound',
        occurredOn,
        asset: {
          storageModifiedName:
            this.readAssetString(asset.storageModifiedName) ?? '',
          sourceUrl: this.readAssetString(asset.sourceUrl),
          downloadUrl: null,
          fileName,
          contentType,
          sizeBytes,
        },
        scanStatus: 'Pending',
        sourceKind: upload.sourceKind ?? 'Upload',
        insight: {
          fileKind: this.resolveOptimisticFileKind(contentType, fileName),
          canRenderInline: this.canRenderInlineOptimistic(contentType),
          shouldInspectForInstructions: this.shouldInspectOptimistic(
            contentType,
            fileName,
          ),
          hasAttachedInstruction: Boolean(
            nullIfEmpty(upload.attachedInstruction),
          ),
          attachedInstruction: upload.attachedInstruction ?? null,
          hasEmbeddedInstructionHints: Boolean(
            nullIfEmpty(upload.extractedTextPreview),
          ),
          embeddedInstructionHint: upload.extractedTextPreview ?? null,
          requiresChunking: (sizeBytes ?? 0) >= 8 * 1024 * 1024,
          handlingMode: 'Reference',
          summary: `Pending attachment ${index + 1}`,
        },
      };
    });
  }

  readAssetString(value: string | null | undefined): string | null {
    return nullIfEmpty(value ?? undefined);
  }

  readAssetNumber(value: number | string | null | undefined): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  resolveOptimisticFileKind(
    contentType: string | null,
    fileName: string | null,
  ): string {
    const normalizedType = nullIfEmpty(contentType)?.toLowerCase();
    if (normalizedType?.startsWith('image/')) {
      return 'Image';
    }

    if (normalizedType?.startsWith('video/')) {
      return 'Video';
    }

    if (normalizedType?.startsWith('audio/')) {
      return 'Audio';
    }

    if (normalizedType?.startsWith('text/')) {
      return 'Text';
    }

    const normalizedName = nullIfEmpty(fileName)?.toLowerCase() ?? '';
    if (
      normalizedName.endsWith('.md') ||
      normalizedName.endsWith('.txt') ||
      normalizedName.endsWith('.json') ||
      normalizedName.endsWith('.csv') ||
      normalizedName.endsWith('.xml')
    ) {
      return 'Document';
    }

    return 'Unknown';
  }

  canRenderInlineOptimistic(contentType: string | null): boolean {
    const normalizedType = nullIfEmpty(contentType)?.toLowerCase();
    return Boolean(
      normalizedType?.startsWith('image/') ||
      normalizedType?.startsWith('video/') ||
      normalizedType?.startsWith('audio/'),
    );
  }

  shouldInspectOptimistic(
    contentType: string | null,
    fileName: string | null,
  ): boolean {
    const kind = this.resolveOptimisticFileKind(contentType, fileName);
    return kind === 'Text' || kind === 'Document' || kind === 'Data';
  }

  normalizeNumericValue(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  normalizeThoughtTraceId(value: unknown): string | null {
    return typeof value === 'string' &&
      value.length > 0 &&
      value === value.trim()
      ? value
      : null;
  }

  buildLiveThoughtTraceId(renderEvent: WebChatRenderEvent): string {
    const correlationId = renderEvent.correlationId;
    if (correlationId) {
      return `live:correlation:${correlationId}`;
    }

    const workRequestId = renderEvent.workRequestId;
    if (workRequestId) {
      return `live:work-request:${workRequestId}`;
    }

    const streamId = renderEvent.streamId;
    if (streamId) {
      return `live:stream:${streamId}:start:${renderEvent.sequence}`;
    }

    return `live:sequence:${renderEvent.sequence}`;
  }

  isGuid(value: string): boolean {
    return isCanonicalUuid(value);
  }

  hasNewInboundResponse(messages: ChatSessionMessageResponse[]): boolean {
    if (!this.lastOutboundTimestamp || messages.length === 0) {
      return false;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (
        msg.direction === 'Inbound' &&
        msg.entryId &&
        !msg.entryId.startsWith('local-')
      ) {
        const msgTime = Date.parse(msg.occurredOn ?? '') || 0;
        if (msgTime >= this.lastOutboundTimestamp - 5000) {
          return true;
        }
      }
    }

    return false;
  }

  isThoughtLikeRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    if (!renderEvent) {
      return false;
    }

    const eventType = renderEvent.eventType;
    const stage = renderEvent.stage;
    return (
      eventType === RealtimeRenderEventType.ConversationThoughtDelta ||
      eventType === RealtimeRenderEventType.ConversationMemoryCaptured ||
      eventType === RealtimeRenderEventType.ConversationCommandProgress ||
      eventType === RealtimeRenderEventType.ConversationPlanDelta ||
      eventType === RealtimeRenderEventType.ConversationSearchHitDelta ||
      eventType === RealtimeRenderEventType.ConversationIntakeProgress ||
      eventType === RealtimeRenderEventType.ConversationExecutionProgress ||
      stage === RealtimeRenderStage.Analysis ||
      stage === ConversationPulseStage.Context ||
      stage === ConversationPulseStage.Reasoning ||
      stage === RealtimeRenderStage.Planning ||
      stage === ConversationPulseStage.Governance ||
      stage === RealtimeRenderStage.Command ||
      stage === RealtimeRenderStage.Search ||
      stage === RealtimeRenderStage.Execution ||
      stage === RealtimeRenderStage.Memory ||
      stage === ConversationPulseStage.ExternalRoute ||
      stage === ConversationPulseStage.NodeWait ||
      stage === ConversationPulseStage.ToolCall ||
      stage === ConversationPulseStage.ProviderCall ||
      stage === RealtimeRenderStage.Presentation ||
      stage === ConversationPulseStage.Delivery ||
      stage === ConversationPulseStage.Waiting ||
      stage === ConversationPulseStage.Terminal
    );
  }

  isSearchRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    if (!renderEvent) {
      return false;
    }

    return (
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationSearchHitDelta ||
      renderEvent.stage === RealtimeRenderStage.Search
    );
  }

  isHealthCheckRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    if (!renderEvent) {
      return false;
    }

    return (
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationHealthCheckDelta ||
      renderEvent.stage === RealtimeRenderStage.HealthCheck
    );
  }

  private get stateSubject(): WebChatStateStore['stateSubject'] {
    return this.state.stateSubject;
  }

  private get lastOutboundTimestamp(): number | null {
    return this.state.lastOutboundTimestamp;
  }

  private patch(partial: Parameters<WebChatStateStore['patch']>[0]): void {
    this.state.patch(partial);
  }
}
